/**
 * DesktopModal — Container (SE-5086)
 *
 * Cross-feature foundation primitive consumed by the 3 desktop modal tickets
 * (SE-5013 Share/Archive/Leave, SE-5017 Add Player Path A/B, SE-5021
 * multi-select search) and likely features/scouting + features/watchlist +
 * features/notes + features/reports. It is the desktop sibling of
 * MobileSheetChrome (SE-5081): both ship the shared Figma chrome and the
 * dismissal paths so each consumer composes content, not chrome.
 *
 * Dismissal paths (all funnel to `onClose`):
 *
 *   1. X close button tap — passes onClose straight to the View.
 *   2. Backdrop / scrim tap — gated by `closeOnBackdrop`. The Container hands
 *      the View `onClose` when true and `undefined` when false, so the
 *      "Discard changes?" flows that must not dismiss on an outside click can
 *      opt out without the View knowing why.
 *   3. ESC key — gated by `closeOnEsc` and wired through the Modal primitive's
 *      native `isKeyboardDismissable` prop.
 *
 * Width resolves to the Figma-pinned pixel sizes (sm=384, md=484 default,
 * lg=640) or a caller-supplied custom number.
 * @module components/DesktopModal
 */

import { useMemo, type ReactNode } from "react";
import type { ViewStyle } from "react-native";

import DesktopModalView from "./DesktopModalView";

/**
 * Named width presets in px, sourced from the Figma "Search Modal" frame
 * (40000732:28566 → md=484) and the SE-5086 ticket (sm=384, lg=640).
 * Exported so consumers and tests can reference the resolved values.
 */
export const DESKTOP_MODAL_WIDTHS = {
  sm: 384,
  md: 484,
  lg: 640,
} as const;

/** Named width preset accepted by the `width` prop. */
export type DesktopModalWidth = keyof typeof DESKTOP_MODAL_WIDTHS;

/**
 * Public props for the DesktopModal primitive.
 *
 * This shape is the stable contract the SE-5013/5017/5021 consumers compose
 * around — renaming or removing fields is a breaking change for them; adding
 * optional fields later is safe.
 */
export interface DesktopModalProps {
  /** Whether the modal is open. Drives the Modal open state. */
  readonly isOpen: boolean;
  /**
   * Dismissal handler. Fired by the X tap, backdrop tap (when enabled), and
   * ESC (when enabled). The receiver must NOT assume a confirm happened — this
   * is a pure "close" signal.
   */
  readonly onClose: () => void;
  /** Title rendered top-left as Heading-2 (Satoshi Bold 18). */
  readonly title: string;
  /**
   * Modal width: a named preset (`sm` 384 / `md` 484 / `lg` 640) or a custom
   * pixel number. Defaults to `md` (484px).
   */
  readonly width?: DesktopModalWidth | number;
  /**
   * Whether a backdrop/scrim tap dismisses the modal. Defaults to true. Pass
   * false for flows that must not lose state on an outside click
   * (e.g. "Discard changes?" prompts).
   */
  readonly closeOnBackdrop?: boolean;
  /** Whether ESC dismisses the modal. Defaults to true. */
  readonly closeOnEsc?: boolean;
  /** Modal body — rendered inside a vertically-scrolling region. */
  readonly children: ReactNode;
  /**
   * Optional sticky footer slot. Stays anchored to the bottom of the modal;
   * body scrolls behind it. Typical use: a primary CTA button row.
   */
  readonly footer?: ReactNode;
}

/**
 * Resolves the `width` prop to a pixel value. A named preset maps through
 * DESKTOP_MODAL_WIDTHS; a number passes through unchanged.
 * @param width - The named-preset-or-number width prop (or undefined → md).
 * @returns The resolved modal width in px.
 */
const resolveWidth = (width: DesktopModalProps["width"]): number => {
  if (typeof width === "number") return width;
  return DESKTOP_MODAL_WIDTHS[width ?? "md"];
};

/**
 * Cross-feature desktop modal primitive.
 * @param props - Component props
 * @param props.isOpen - Whether the modal is currently open
 * @param props.onClose - Unified dismissal callback (X / backdrop / ESC)
 * @param props.title - Title rendered top-left
 * @param props.width - Named preset or custom px width (default md / 484px)
 * @param props.closeOnBackdrop - Whether a scrim tap dismisses (default true)
 * @param props.closeOnEsc - Whether ESC dismisses (default true)
 * @param props.children - Modal body, rendered inside a scroll region
 * @param props.footer - Optional sticky footer node
 */
const DesktopModalContainer = ({
  isOpen,
  onClose,
  title,
  width,
  closeOnBackdrop = true,
  closeOnEsc = true,
  children,
  footer,
}: DesktopModalProps) => {
  // design-system: dynamic-style waiver — Figma pins the modal to exact pixel
  // widths (484/384/640 or a caller-supplied custom width) that have no sizing
  // token; a data-driven inline width is the only faithful path. Computed here
  // (not in the View) so the View stays arrow-shorthand and the style object
  // identity is stable across re-renders.
  const contentStyle = useMemo<ViewStyle>(
    () => ({ width: resolveWidth(width) }),
    [width]
  );

  return (
    <DesktopModalView
      isOpen={isOpen}
      onClose={onClose}
      onBackdropPress={closeOnBackdrop ? onClose : undefined}
      isKeyboardDismissable={closeOnEsc}
      title={title}
      contentStyle={contentStyle}
      footer={footer}
    >
      {children}
    </DesktopModalView>
  );
};

export default DesktopModalContainer;
